import React from "react";
import styled from "styled-components";
import { useLocation } from "react-router-dom";
import { PageWrapperTwoColumn, Spinner } from "@cinesys/ui/components";
import Banner from "./shared/Banner";
import AbHeader, { AbModeNav } from "./AbHeader";
import { AB_VAULT_INTERFACE_ID } from "./config";
import { useInterfaceAccess } from "../../hooks/useInterfaceAccess";
import MyPlaceholdersPanel from "./shared/MyPlaceholdersPanel";

// `CustomerLayout`'s <main> is a bounded flex column; the root joins it and the content area
// takes the remaining height under the header.
const ScreenRoot = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  color: #fff;
`;

const Workspace = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
`;

const MainColumn = styled.div`
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const TwoColumn = styled(PageWrapperTwoColumn)`
  flex: 1 1 auto;
  min-height: 0;

  & > div:first-child,
  & > div:last-child {
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  & > div:last-child {
    overflow-y: auto;
  }
`;

const AsidePad = styled.div`
  padding: 20px 22px 20px 0;
`;

const ScreenContent = styled.div<{ $scroll?: boolean; $aside?: boolean }>`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: ${(p) =>
    p.$scroll ? (p.$aside ? "0 10px 0 22px" : "0 22px") : "20px 22px 40px"};
  box-sizing: border-box;
  overflow-y: ${(p) => (p.$scroll ? "auto" : "visible")};
`;

const ScreenFooter = styled.footer<{ $aside?: boolean }>`
  position: sticky;
  bottom: 0;
  z-index: 5;
  flex: 0 0 auto;
  margin: 0 ${(p) => (p.$aside ? -10 : -22)}px 0 -22px;
  padding: 15px ${(p) => (p.$aside ? 10 : 22)}px 15px 22px;
  background: var(--dark100);
  border-top: 1px solid var(--abField, rgba(255, 255, 255, 0.12));
  box-shadow: 0 -10px 24px rgba(0, 0, 0, 0.25);
  --buttonFilled: 100%;
`;

const FooterInner = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 15px;
  min-height: 40px;
`;

const FooterBanner = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  justify-content: flex-start;
`;

const ScreenCenter = styled.div`
  flex: 1 1 auto;
  min-height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface ScreenShellProps {
  loading?: boolean;
  /** Shown centered in place of `loading`/`children`, e.g. the interface-access notice. */
  notice?: React.ReactNode;
  /** Let the content area scroll (long forms); table screens manage their own scrolling. */
  scroll?: boolean;
  /** Submit actions, pinned to the bottom of the screen while the form scrolls. */
  footer?: React.ReactNode;
  /** Shown above the footer actions after a failed submit or a failed validation. */
  footerError?: string | null;
  children: React.ReactNode;
}

const AbScreenShell = ({
  loading,
  notice,
  scroll,
  footer,
  footerError,
  children,
}: ScreenShellProps) => {
  const { pathname } = useLocation();
  const access = useInterfaceAccess(AB_VAULT_INTERFACE_ID);
  const showAside =
    !notice &&
    access.status === "granted" &&
    !pathname.startsWith("/ab-vault/candidate");

  const main = (
    <MainColumn>
      <ScreenContent $scroll={scroll} $aside={showAside}>
        {notice ? (
          <ScreenCenter>{notice}</ScreenCenter>
        ) : loading ? (
          <ScreenCenter>
            <Spinner />
          </ScreenCenter>
        ) : (
          <>
            <AbModeNav />
            {children}
            {footer && (
              <ScreenFooter $aside={showAside}>
                <FooterInner>
                  {footerError && (
                    <FooterBanner>
                      <Banner error={footerError} />
                    </FooterBanner>
                  )}
                  {footer}
                </FooterInner>
              </ScreenFooter>
            )}
          </>
        )}
      </ScreenContent>
    </MainColumn>
  );

  return (
    <ScreenRoot>
      <AbHeader />
      {showAside ? (
        <TwoColumn
          id="ab-vault-tools"
          padding="0"
          minWidth="900px"
          defaultRatio={0.7}
        >
          {main}
          <AsidePad>
            <MyPlaceholdersPanel />
          </AsidePad>
        </TwoColumn>
      ) : (
        <Workspace>{main}</Workspace>
      )}
    </ScreenRoot>
  );
};

export default AbScreenShell;
